package org.teddapp.project.ui

import org.teddapp.project.db.TODAY_ISO
import org.teddapp.project.db.civilFromDays

/**
 * Calendrier sans dépendance : conversions ISO ↔ jours depuis l'époque (algorithme Hinnant),
 * décalages usuels et formats français courts. Tout est pur, testable sans Compose.
 */
object TeddDates {
    private val DAYS_FR = listOf("lun.", "mar.", "mer.", "jeu.", "ven.", "sam.", "dim.")
    private val DAY_INITIALS = listOf("L", "M", "M", "J", "V", "S", "D")
    private val MONTHS_FR = listOf(
        "janv.", "févr.", "mars", "avr.", "mai", "juin",
        "juil.", "août", "sept.", "oct.", "nov.", "déc.",
    )
    val MONTHS_LONG_FR = listOf(
        "janvier", "février", "mars", "avril", "mai", "juin",
        "juillet", "août", "septembre", "octobre", "novembre", "décembre",
    )

    val weekdayInitials: List<String> get() = DAY_INITIALS
    val todayIso: String get() = TODAY_ISO

    /** Jours depuis 1970-01-01 pour une date civile (Hinnant, days_from_civil). */
    fun daysFromCivil(y0: Int, m: Int, d: Int): Int {
        val y = if (m <= 2) y0 - 1 else y0
        val era = (if (y >= 0) y else y - 399) / 400
        val yoe = y - era * 400
        val mp = if (m > 2) m - 3 else m + 9
        val doy = (153 * mp + 2) / 5 + d - 1
        val doe = yoe * 365 + yoe / 4 - yoe / 100 + doy
        return era * 146097 + doe - 719468
    }

    fun isoFromDays(days: Int): String {
        val (y, m, d) = civilFromDays(days)
        return "${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${d.toString().padStart(2, '0')}"
    }

    /** null si la chaîne n'est pas une date ISO valide. */
    fun daysFromIso(iso: String?): Int? {
        if (iso == null || iso.length < 10) return null
        val y = iso.substring(0, 4).toIntOrNull() ?: return null
        val m = iso.substring(5, 7).toIntOrNull() ?: return null
        val d = iso.substring(8, 10).toIntOrNull() ?: return null
        if (m !in 1..12 || d !in 1..31) return null
        return daysFromCivil(y, m, d)
    }

    fun isValidIso(iso: String?): Boolean = daysFromIso(iso) != null

    fun addDays(iso: String, n: Int): String = isoFromDays((daysFromIso(iso) ?: return iso) + n)

    fun daysInMonth(y: Int, m: Int): Int = when (m) {
        1, 3, 5, 7, 8, 10, 12 -> 31
        4, 6, 9, 11 -> 30
        else -> if ((y % 4 == 0 && y % 100 != 0) || y % 400 == 0) 29 else 28
    }

    fun endOfMonth(iso: String): String {
        val y = iso.substring(0, 4).toInt(); val m = iso.substring(5, 7).toInt()
        return "${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}-${daysInMonth(y, m)}"
    }

    fun endOfYear(iso: String): String = "${iso.substring(0, 4)}-12-31"

    /** 0 = lundi … 6 = dimanche. */
    fun weekdayMondayZero(days: Int): Int = (((days % 7) + 7) % 7 + 3) % 7

    /** "mer. 30 sept." ou "mer. 30 sept. 2026". */
    fun formatShort(iso: String?, withYear: Boolean = false): String? {
        val days = daysFromIso(iso) ?: return iso?.takeIf { it.isNotBlank() }
        val (y, m, d) = civilFromDays(days)
        val base = "${DAYS_FR[weekdayMondayZero(days)]} $d ${MONTHS_FR[m - 1]}"
        return if (withYear) "$base $y" else base
    }

    /** Étiquette relative : « aujourd'hui », « demain », « dans 5 j », « il y a 3 j », sinon la date courte. */
    fun formatRelative(iso: String?, today: String = TODAY_ISO): String? {
        val target = daysFromIso(iso) ?: return null
        val now = daysFromIso(today) ?: return formatShort(iso)
        return when (val delta = target - now) {
            0 -> "aujourd'hui"
            1 -> "demain"
            -1 -> "hier"
            in 2..6 -> "dans $delta j"
            in -6..-2 -> "il y a ${-delta} j"
            else -> formatShort(iso)
        }
    }

    fun monthTitle(y: Int, m: Int): String = "${MONTHS_LONG_FR[m - 1]} $y"

    /**
     * Grille d'un mois : 6 semaines × 7 jours à partir du lundi, chaque case = ISO ou null hors mois.
     */
    fun monthGrid(y: Int, m: Int): List<String?> {
        val first = daysFromCivil(y, m, 1)
        val lead = weekdayMondayZero(first)
        val count = daysInMonth(y, m)
        val cells = ArrayList<String?>(42)
        repeat(lead) { cells.add(null) }
        for (d in 1..count) cells.add(isoFromDays(first + d - 1))
        while (cells.size < 42) cells.add(null)
        return cells
    }
}
